import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    CircularProgress,
    Typography,
    Snackbar,
    Alert,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axiosInstance from '../utils/axios';
import Cookies from "js-cookie";
import { decodeJWT } from '../utils/jwt';

function ResetPassword() {
    const navigate = useNavigate();


    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [helperText, setHelperText] = useState('');
    const [userDetails, setUserDetails] = useState({})

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        const token = Cookies.get("access_token");
        if (!token) {
            navigate("/login", { replace: true });
        } else {
            const decoded = decodeJWT(token);
            setUserDetails(decoded);
        }
         // eslint-disable-next-line
    }, [])

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            setBtnLoading(true);

            if (newPassword !== confirmPassword) {
                setSnackbar({
                    open: true,
                    message: "Password mismatch",
                    severity: "error",
                });
                return;
            }

            let temp = {};

            temp.email = userDetails?.email
            temp.new_password = confirmPassword;

            const res = await axiosInstance.post(`/auth/reset-password`, temp);

            if (res.status === 200) {
                setSnackbar({
                    open: true,
                    message: "Password Updated Successfully",
                    severity: "success",
                });

                setTimeout(() => {
                    navigate("/login");
                }, 1000);
            } else {
                setSnackbar({
                    open: true,
                    message: "Something went wrong",
                    severity: "error",
                });
            }
        } catch (err) {
            console.error(err);
            setSnackbar({
                open: true,
                message: "Something went wrong",
                severity: "error",
            });
        } finally {
            setBtnLoading(false);
        }
    };

    return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#f5f5f5',
            }}
        >
            <Box
                component="form"
                onSubmit={onSubmit}
                sx={{
                    width: 432,
                    p: 4,
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    boxShadow: 3,
                }}
            >
                <Box sx={{ mb: 5, textAlign: 'center' }}>
                    <img src="/suryalogo.svg" alt="Surya Logo" width={230} height={50} />
                </Box>

                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 'bold',
                        mb: 3,
                        textAlign: 'center',
                        fontSize: '24px'
                    }}
                >
                    Change Password
                </Typography>

                <TextField
                    fullWidth
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter New Password"
                    variant="outlined"
                    required
                    value={newPassword}
                    onChange={(e) => {
                        const value = e.target.value;
                        setNewPassword(value);
                        if (confirmPassword && value !== confirmPassword) {
                            setPasswordError(true);
                            setHelperText("Passwords do not match");
                        } else {
                            setPasswordError(false);
                            setHelperText('');
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowNewPassword((prev) => !prev)}
                                    edge="end"
                                >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />

                <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-Enter New Password"
                    variant="outlined"
                    required
                    value={confirmPassword}
                    error={passwordError}
                    helperText={passwordError ? helperText : ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        setConfirmPassword(value);
                        if (newPassword && value !== newPassword) {
                            setPasswordError(true);
                            setHelperText("Passwords do not match");
                        } else {
                            setPasswordError(false);
                            setHelperText('');
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    edge="end"
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="error"
                    sx={{
                        height: 59,
                        borderRadius: '30px',
                        fontSize: "20px",
                        fontWeight: 600
                    }}
                    disabled={btnLoading || !newPassword || !confirmPassword || passwordError}

                >
                    {btnLoading ? <CircularProgress size={24} color="inherit" /> : "Save"}
                </Button>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ResetPassword;